<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @button-click="handleButtonClick"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import DataTable from '../../grid/DataTable.vue';
import { useSOReportAttributes } from '../../composables/useSOReportAttributes';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

const emit = defineEmits<{
  edit: [id: Id];
  delete: [id: Id];
}>();

const { t } = useI18n();
const { save } = useSOReportAttributes();

const datatableRef = ref<DataTableInstance | null>(null);

/*
 * `incluir_inativos=1` — esta é A tela que gerencia os atributos, e por isso a
 * única que enxerga os desativados. Sem o parâmetro, desativar um atributo o
 * fazia sumir daqui e não havia como reativá-lo (chamado 2026-0034); as telas
 * de laudo continuam recebendo só os ativos, que é o padrão do backend.
 */
const endpoint = computed<string>(
  () => '/index.php/so/report_attributes/lista?incluir_inativos=1',
);

const tableColumns = computed(() => {
  return [
    {
      data: 'so_ra_id',
      title: t('ID'),
      width: 100,
      sortable: true,
      filterType: 'text',
      renderType: 'clickable_id'
    },
    {
      data: 'so_ra_name',
      title: t('Nome'),
      width: 400,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'so_ra_type',
      title: t('Tipo'),
      width: 200,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'so_ra_active',
      title: t('Situação'),
      width: 120,
      sortable: true,
      renderType: 'active_inactive'
    },
    {
      /*
       * Botão, não ToggleSwitch: `so_ra_active` chega como 1/0 e o
       * `renderType: 'toggle'` liga o modelValue direto no ToggleSwitch do
       * PrimeVue, que só se considera ligado com `true` booleano — um 1 ficaria
       * eternamente desenhado como desligado. Mesmo par (coluna de situação +
       * botão) do SetupFeeMarketsTable, pelo mesmo motivo.
       */
      data: null,
      title: t('Ativar/Desativar'),
      width: 150,
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Ativar/Desativar'),
      buttonIcon: 'pi pi-power-off',
      buttonAction: 'toggle-active',
      buttonTestId: (row: Record<string, unknown>) =>
        `so-report-attributes-toggle-${row.so_ra_id}`
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `so_report_attributes_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV')
  };
});

const loadData = (): void => {
  const stack = new Error().stack;
  log('SOReportAttributesTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('SOReportAttributesTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t('Carregados') + ` ${data.length} ` + t('registros de atributos de laudo'));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('SOReportAttributesTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : (error.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar atributos de laudo') + ': ' + message);
};

const handleEditClick = (id: Id): void => {
  log('SOReportAttributesTable', 'handleEditClick', 'Edit clicked for ID', id);
  emit('edit', id);
};

interface AtributoRow {
  so_ra_id: number;
  so_ra_name: string;
  so_ra_type: string;
  so_ra_active: number;
}

/**
 * Liga/desliga o atributo (chamado 2026-0034). Desligado ele some das telas de
 * laudo mas CONTINUA nesta lista — é isto que torna a ação reversível, e é a
 * diferença entre desativar e o antigo "remover", que escondia para sempre.
 *
 * Reaproveita o `salvar` em vez de um endpoint próprio: o backend só mexe em
 * `so_ra_active` quando o campo vem no corpo.
 */
const handleToggleActive = async (row: AtributoRow): Promise<void> => {
  const ativar = Number(row.so_ra_active) !== 1;

  try {
    await save({
      so_ra_id: Number(row.so_ra_id),
      so_ra_name: row.so_ra_name,
      so_ra_type: row.so_ra_type,
      so_ra_active: ativar ? 1 : 0,
    });

    eventBus.emit(
      'globalMessage.success',
      ativar ? t('Atributo de laudo ativado') : t('Atributo de laudo desativado'),
    );
    loadData();
  } catch (err) {
    log('SOReportAttributesTable', 'handleToggleActive', 'Error', err);
    eventBus.emit('globalMessage.error', t('Erro ao alterar a situação do atributo de laudo'));
  }
};

const handleButtonClick = (e: { action?: string; data?: AtributoRow }): void => {
  if (e.action === 'toggle-active' && e.data) {
    void handleToggleActive(e.data);
  }
};

const handleSaved = (): void => {
  log('SOReportAttributesTable', 'event:so-report-attributes.saved', 'Received');
  loadData();
};

const handleDeleted = (): void => {
  log('SOReportAttributesTable', 'event:so-report-attributes.deleted', 'Received');
  loadData();
};

onMounted(() => {
  log('SOReportAttributesTable', 'mounted', 'Setting up event listeners');
  log('SOReportAttributesTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  eventBus.on('so-report-attributes.saved', handleSaved);
  eventBus.on('so-report-attributes.deleted', handleDeleted);
});

onBeforeUnmount(() => {
  log('SOReportAttributesTable', 'beforeUnmount', 'Cleaning up event listeners');

  eventBus.off('so-report-attributes.saved', handleSaved);
  eventBus.off('so-report-attributes.deleted', handleDeleted);
});

defineExpose({
  loadData
});
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
